'use client';

import React, { useEffect, useRef, useState } from 'react';
import { IdeaBankItem } from './IdeaCard';

interface Props {
  idea: IdeaBankItem;
  tokenBalance: number | null;
  onClose: (updatedTitle: string, updatedObjective: string) => void;
  onDiagnose: (title: string, objective: string) => Promise<void>;
  isDiagnosing: boolean;
}

export default function DiagnoseModal({ idea, tokenBalance, onClose, onDiagnose, isDiagnosing }: Props) {
  const [title, setTitle] = useState(idea.title);
  const [objective, setObjective] = useState(idea.objective);
  const [showConfirm, setShowConfirm] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Bloquear scroll del body
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = () => onClose(title, objective);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) handleClose();
  };

  const tags = [idea.sector, idea.tipo, idea.modelo, idea.innovacion, ...(idea.impacto ?? [])].filter(Boolean) as string[];

  const DIAGNOSE_COST = 5;
  const canDiagnose = tokenBalance === null || tokenBalance >= DIAGNOSE_COST;

  if (showConfirm) {
    return (
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      >
        <div className="bg-[#1a0a30] border border-white/15 rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center space-y-5">
          <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-2xl">
            🔬
          </div>
          <div>
            <h3 className="text-white font-bold text-lg mb-1">¿Validemos la idea?</h3>
            <p className="text-white/60 text-sm">
              La IA generará un breve análisis para luego seguir desarrollando tu idea junto a nuestro <strong className="text-white">Coach AI-de</strong>.
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-left space-y-1">
            <p className="text-white font-medium text-sm truncate">{title}</p>
            <p className="text-white/50 text-xs line-clamp-2">{objective}</p>
          </div>
          <div className="flex items-center justify-center gap-2 text-amber-300 text-sm font-medium">
            <span>🪙</span>
            <span>Costo: {DIAGNOSE_COST} tokens</span>
            {tokenBalance !== null && (
              <span className="text-white/40">· Saldo: {tokenBalance}</span>
            )}
          </div>
          {!canDiagnose && (
            <p className="text-red-400 text-xs">No tienes suficientes tokens. Recarga tu saldo para continuar.</p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              className="flex-1 py-2.5 rounded-xl border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-all text-sm"
            >
              Volver
            </button>
            <button
              type="button"
              disabled={!canDiagnose || isDiagnosing}
              onClick={() => onDiagnose(title, objective)}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold text-sm hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isDiagnosing ? (
                <>
                  <span className="animate-spin">⟳</span>
                  <span>Analizando…</span>
                </>
              ) : (
                <>✨ Confirmar</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
    >
      <div className="bg-[#160928] border border-white/12 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/8">
          <h2 className="text-white font-bold text-lg">Detalle de la idea</h2>
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-all flex items-center justify-center text-lg"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-white/8 border border-white/10 text-white/50">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Title editable */}
          <div>
            <label className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-1.5 block">
              Título
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-white/5 border border-white/12 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-orange-400/50 focus:bg-white/8 transition-all text-sm font-medium"
              placeholder="Título de tu idea"
            />
          </div>

          {/* Objective editable */}
          <div>
            <label className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-1.5 block">
              Objetivo
            </label>
            <textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              rows={3}
              className="w-full bg-white/5 border border-white/12 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-orange-400/50 focus:bg-white/8 transition-all text-sm resize-none"
              placeholder="¿Qué quieres lograr con esta idea?"
            />
          </div>

          {/* Info box */}
          <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/20 rounded-xl p-4">
            <p className="text-white/80 text-sm leading-relaxed">
              <span className="text-orange-300 font-semibold">Diagnosticar con IA</span> genera automáticamente el análisis completo de tu idea (problema, solución, cliente, monetización) y te lleva directo al <span className="text-pink-300 font-semibold">AIde Coach</span>.
            </p>
            <p className="text-white/40 text-xs mt-2">
              Te saltarás los 6 pasos del formulario. Los cambios al título y objetivo se guardan al cerrar.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 py-3 rounded-xl border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition-all text-sm font-medium"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={!title.trim() || !objective.trim()}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold text-sm hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <span>🔬</span>
            <span>Diagnosticar mi idea</span>
            <span className="text-white/70 text-xs">({DIAGNOSE_COST} tokens)</span>
          </button>
        </div>
      </div>
    </div>
  );
}
